import { Box, Divider, Typography } from '@mui/material';
import PageLoadProgress from '@shared/components/PageLoadProgress';
import theme from '@shared/themes/darkTheme';
import { VideoPlayer } from '../../widgets/videos/components/VideoPlayer';
import { GenresCarousel } from '../components/GenreCarousel';
import { LiveButton } from '../components/LiveButton';
import { NewsBackground } from '../components/NewsBackground';
import { ProgrammesCarousel } from '../components/ProgrammesCarousel';
import { ProgrammesGrid } from '../components/ProgrammesGrid';
import { UpgradeBanner } from '../components/upgradeBanner';
import { VideoCarousel } from '../components/VideoCarousel';
import { VideosGrid } from '../components/VideosGrid';
import { useFreemiumNews } from '../hooks/useFreemiumNews';
import { NavigationLevel, Show } from '../types/types';

export const FreemiumNews = () => {
  const {
    isFreemiumUser,
    selectedVideoId,
    mute,
    isLive,
    source,
    latestVids,
    onVideoClick,
    onProgrammeClick,
    onCategoryClick,
    handleBackClick,
    onLiveClick,
    navigationLevel,
    mediaGenres,
    mediaContent,
    selectedProgramme,
    selectedCategory,
    hasMoreMediaContent,
    loadMoreMediaContent,
    isLoading,
    newVideoIds,
    isApiAvailable,
    isApiLoading,
    apiError,
    selectedVideoTitle,
  } = useFreemiumNews();
  if (!isApiAvailable || isApiLoading) {
    return (
      <Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <PageLoadProgress size={40} />
      </Box>
    );
  }
  if (apiError) {
    return (
      <Box sx={{ width: '100%', p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: 'red' }}>
          There was an error loading the news page, please try again later.
        </Typography>
      </Box>
    );
  }
  return (
    <>
      <NewsBackground />
      <Box
        sx={{
          width: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <img src="/assets/flux_logo_blue.png" alt="Freemium Logo" width={300} height={100} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {isFreemiumUser && <UpgradeBanner />}
          <Box
            sx={{
              width: '100%',
              minWidth: { xs: 'unset' },
              display: 'flex',
            }}
          >
            <Box
              sx={{
                width: { xs: '100%' },
                minWidth: { xs: 'unset' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: { xs: '100%', sm: '70%', md: '55%' },
                    display: 'flex',
                    mb: 2,
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <VideoPlayer
                    videoId={selectedVideoId || 'live'}
                    videoIndex={0}
                    numberOfVideos={1}
                    showControlBtn={false}
                    mute={mute}
                    isLive={isLive}
                    source={source}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        left: '50%',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                      }}
                    >
                      {isLive ? 'Flux Live' : selectedVideoTitle || ''}
                    </Typography>
                    <Box sx={{ flex: '0 0 auto' }}>
                      <LiveButton isLive={isLive} onLiveClick={onLiveClick} />
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row-reverse', pl: '2%', pr: '2%' }}>
                {/* <VideoSearch onSearch={onSearch} value={searchQuery} /> */}
              </Box>
              <Divider sx={{ width: '100%', mb: 2, mt: 2 }} />
              {navigationLevel === NavigationLevel.CATEGORIES && (
                <Box sx={{ pl: '2%', pr: '2%' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Our Latest Releases
                  </Typography>
                  {latestVids.length !== 0 && (
                    <VideoCarousel
                      videos={latestVids}
                      onVideoClick={(videoId: string, source: string) => onVideoClick(videoId, source)}
                      selectedVideoId={selectedVideoId}
                      newVideoIds={newVideoIds}
                    />
                  )}
                </Box>
              )}
              {navigationLevel === NavigationLevel.SEARCH ? (
                <VideosGrid
                  mediaContent={mediaContent}
                  selectedProgramme={null}
                  handleBackClick={handleBackClick}
                  onVideoClick={(videoId: string) => onVideoClick(videoId, mediaContent.find(video => video.id === videoId)?.source || 'youtube')}
                  selectedVideoId={selectedVideoId}
                  theme={theme}
                  isLoading={isLoading}
                  hasMoreMediaContent={hasMoreMediaContent}
                  loadMoreMediaContent={loadMoreMediaContent}
                />
              ) : (
                <Box sx={{ width: '100%', pl: '2%', pr: '2%' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    All Shows
                  </Typography>
                  <ProgrammesCarousel
                    programmes={mediaGenres.flatMap(genre => genre.shows)}
                    onProgrammeClick={onProgrammeClick}
                    selectedProgrammeName={selectedProgramme?.name || ''}
                  />{' '}
                </Box>
              )}

              {navigationLevel === NavigationLevel.CATEGORIES && (
                <>
                  <Box sx={{ pl: '2%', pr: '2%' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      Browse by Category
                    </Typography>
                    <GenresCarousel genres={mediaGenres} onCategoryClick={onCategoryClick} />
                  </Box>
                </>
              )}
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, pl: '2%', pr: '2%' }}>
                {navigationLevel === NavigationLevel.SHOWS && (
                  <ProgrammesGrid
                    mediaGenres={mediaGenres}
                    selectedCategory={selectedCategory}
                    handleBackClick={handleBackClick}
                    onProgrammeClick={(programme: Show) => onProgrammeClick(programme, true)}
                  />
                )}
                {navigationLevel === NavigationLevel.VIDEOS && !!selectedProgramme && (
                  <VideosGrid
                    mediaContent={[...mediaContent].filter(video => video.type === 'video')}
                    selectedProgramme={selectedProgramme}
                    handleBackClick={handleBackClick}
                    onVideoClick={(videoId: string) => onVideoClick(videoId, mediaContent.find(video => video.id === videoId)?.source || 'youtube')}
                    selectedVideoId={selectedVideoId}
                    theme={theme}
                    isLoading={isLoading}
                    hasMoreMediaContent={hasMoreMediaContent}
                    loadMoreMediaContent={loadMoreMediaContent}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
